import React, { FormEvent, useMemo, useState } from "react";
import {
  Input,
  Button,
  Text,
  makeStyles,
  tokens,
  Field,
} from "@fluentui/react-components";
import { createEmailFormValidation } from "./EmailForm.validator";
import { ValidationErrors } from "../../../types/common";
import { validateField, validateForm } from "../Form.validators";
import { ValidatableValue } from "../Form.types";
import { BaseEmailFormParamsKeys, EmailFormParams } from "../../../types/login";

/* global HTMLFormElement */

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    padding: "12px",
  },
  title: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
  },
});

interface EmailFormProps {
  error?: string;
  saving?: boolean;
  onClearError: () => void;
  onSubmit: (params: EmailFormParams) => void;
}

type FormRecord = Record<keyof EmailFormParams, ValidatableValue>;

const EmailForm: React.FC<EmailFormProps> = ({
  error,
  saving,
  onClearError,
  onSubmit,
}) => {
  const styles = useStyles();
  const [formData, setFormData] = useState<EmailFormParams>({
    email: "",
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );

  const validationConfig = useMemo(() => createEmailFormValidation(), []);

  const handleChange = (field: BaseEmailFormParamsKeys, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (error) onClearError();

    const fieldConfig = validationConfig[field];
    if (fieldConfig) {
      const result = validateField<string>(value, fieldConfig);
      if (!result.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          [field]: result.error,
        }));
      } else {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = validateForm<FormRecord>(
      formData as FormRecord,
      validationConfig,
    );
    setValidationErrors(errors);

    if (Object.keys(errors).length === 0 && !saving) {
      onSubmit(formData);
    }
  };

  const isSaveDisabled = Object.keys(validationErrors).length > 0;

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <Text className={styles.title}>Enter Email</Text>

      <Field
        id="email"
        label="Email"
        validationState={validationErrors.email ? "error" : "none"}
        validationMessage={validationErrors.email}
      >
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          placeholder="Enter your email address"
          required
        />
      </Field>

      {error && (
        <Field
          id="apiErrorMessage"
          validationState="error"
          validationMessage={error}
        />
      )}

      <Button
        appearance="primary"
        type="submit"
        disabled={!!error || isSaveDisabled || saving}
      >
        Continue
      </Button>
    </form>
  );
};

export default EmailForm;
